import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

interface BurndownDataPoint {
  date: string;
  ideal: number;
  actual: number;
}

interface BurnDownChartProps {
  totalBudgetHours: number;
  actualHoursUsed: number;
  daysInPeriod?: number;
  serverData?: BurndownDataPoint[];
}

export default function BurnDownChart({ totalBudgetHours, actualHoursUsed, daysInPeriod = 30, serverData }: BurnDownChartProps) {
  const { t } = useTranslation();
  const remaining = Math.max(0, totalBudgetHours - actualHoursUsed);
  const pct = totalBudgetHours > 0 ? (remaining / totalBudgetHours) * 100 : 100;
  const alertColor = pct < 10 ? 'text-danger-600' : pct < 20 ? 'text-warning-600' : 'text-success-600';

  const data = serverData && serverData.length > 0
    ? serverData
    : Array.from({ length: daysInPeriod + 1 }, (_, i) => {
        const idealRemaining = totalBudgetHours - (totalBudgetHours / daysInPeriod) * i;
        const dayProgress = i / daysInPeriod;
        const actualRemaining = totalBudgetHours - (actualHoursUsed * dayProgress);
        return {
          date: `${t('charts.day')}${i}`,
          ideal: Math.max(0, parseFloat(idealRemaining.toFixed(1))),
          actual: Math.max(0, parseFloat(actualRemaining.toFixed(1))),
        };
      });

  const warningThreshold20 = totalBudgetHours * 0.2;
  const warningThreshold10 = totalBudgetHours * 0.1;

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-surface-700">{t('charts.burndown')}</h3>
        <span className={`text-sm font-bold ${alertColor}`}>
          {t('charts.hoursRemainingWithPct', { hours: remaining.toFixed(1), pct: pct.toFixed(0) })}
        </span>
      </div>

      {pct < 20 && (
        <div className={`px-3 py-2 rounded-lg text-xs font-medium mb-3 ${
          pct < 10 ? 'bg-danger-50 text-danger-700 border border-danger-100' : 'bg-warning-50 text-warning-700 border border-warning-200'
        }`}>
          {pct < 10 ? `⚠️ ${t('charts.lessThan10')}` : `⚠️ ${t('charts.lessThan20')}`}
        </div>
      )}

      <ResponsiveContainer width="100%" height={250}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip contentStyle={{ direction: 'rtl', fontSize: 12 }} />
          <ReferenceLine y={warningThreshold20} stroke="#f59e0b" strokeDasharray="5 5" label="" />
          <ReferenceLine y={warningThreshold10} stroke="#ef4444" strokeDasharray="5 5" label="" />
          <Line type="monotone" dataKey="ideal" stroke="#94a3b8" strokeDasharray="5 5" name={t('charts.idealRate')} dot={false} />
          <Line type="monotone" dataKey="actual" stroke="#3b82f6" strokeWidth={2} name={t('charts.actual')} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
